import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiUsers, FiShoppingBag, FiDollarSign, FiClock, FiCheckCircle } from 'react-icons/fi';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function SalesDataPage() {
  const [reminders, setReminders] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSales: 0,
    activeReminders: 0,
    completedJobs: 0,
    commission: 0,
    pendingJobs: 0
  });

  useEffect(() => {
    fetchSalesData();
  }, []);

  const fetchSalesData = async () => {
    setIsLoading(true);
    try {
      // 1. ดึงคิวงานแจ้งเตือนล้างแอร์
      const resRem = await fetch(`${API_URL}?action=get_sales_reminders`);
      const dataRem = await resRem.json();
      const listRem = dataRem.success ? dataRem.data : [];
      setReminders(listRem);

      // 2. ดึงคิวงานทั้งหมดเพื่อวิเคราะห์สถิติจริง
      const resJobs = await fetch(`${API_URL}?action=get_all_jobs`);
      const dataJobs = await resJobs.json();
      const listJobs = dataJobs.success ? dataJobs.data : [];
      setJobs(listJobs);

      // 3. คำนวณสถิติ
      const completed = listJobs.filter(j => j.status === 'completed');
      const pending = listJobs.filter(j => j.status === 'pending_approval' || j.status === 'assigned');
      const salesSum = completed.reduce((sum, j) => sum + (Number(j.price) || 0), 0);
      const commSum = salesSum * 0.05; // 🚀 Commission 5%

      setStats({
        totalSales: salesSum,
        activeReminders: listRem.length,
        completedJobs: completed.length,
        pendingJobs: pending.length,
        commission: commSum
      });
    } catch (err) {
      console.error(err);
      Swal.fire('ข้อผิดพลาด', 'ดึงข้อมูลจาก API หลังบ้านไม่สำเร็จ', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // จัดการข้อมูลแสดงในชาร์ต (แบ่งตามประเภทงาน)
  const getJobTypeChartData = () => {
    const counts = {};
    jobs.forEach(j => {
      const type = j.job_type || 'ทั่วไป';
      counts[type] = (counts[type] || 0) + 1;
    });
    return Object.keys(counts).map(key => ({
      name: key === 'aircon' ? 'ล้างแอร์' : key === 'rework' ? 'งานแก้' : key === 'general' ? 'ซ่อมทั่วไป' : key,
      value: counts[key]
    }));
  };

  const chartData = getJobTypeChartData();
  const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444'];

  return (
    <div className="space-y-6 pb-10 text-slate-800 dark:text-slate-100">
      <div>
        <h1 className="text-2xl font-black">📈 รายงานข้อมูลการขายและฝั่งแอปเซลส์ (Sales Analytics)</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">รายงานสรุปยอดขายของเซลส์ ประวัติการจอง ตลอดจนการติดตามคิวล้างแอร์ของลูกค้าเก่า</p>
      </div>

      {isLoading ? (
        <div className="text-center py-20 text-indigo-500 font-bold">กำลังประมวลผลสรุปยอดเซลส์...</div>
      ) : (
        <>
          {/* 📊 Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="bg-white dark:bg-[#1e293b] p-5 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-bold text-gray-400">ยอดขายเซลส์ทั้งหมด</span>
                <p className="text-2xl font-black text-indigo-500 mt-1">฿{stats.totalSales.toLocaleString()}</p>
              </div>
              <div className="bg-indigo-500/10 text-indigo-500 p-3 rounded-lg"><FiTrendingUp size={20} /></div>
            </div>

            <div className="bg-white dark:bg-[#1e293b] p-5 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-bold text-gray-400">ค่าคอมมิชชันทีมเซลส์ (5%)</span>
                <p className="text-2xl font-black text-emerald-500 mt-1">฿{stats.commission.toLocaleString()}</p>
              </div>
              <div className="bg-emerald-500/10 text-emerald-500 p-3 rounded-lg"><FiDollarSign size={20} /></div>
            </div>

            <div className="bg-white dark:bg-[#1e293b] p-5 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-bold text-gray-400">ลูกค้าที่ครบกำหนดล้างแอร์ (6 ด.)</span>
                <p className="text-2xl font-black text-amber-500 mt-1">{stats.activeReminders} ราย</p>
              </div>
              <div className="bg-amber-500/10 text-amber-500 p-3 rounded-lg"><FiClock size={20} /></div>
            </div>

            <div className="bg-white dark:bg-[#1e293b] p-5 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-bold text-gray-400">งานที่สำเร็จและส่งมอบ</span>
                <p className="text-2xl font-black text-blue-500 mt-1">{stats.completedJobs} งาน</p>
              </div>
              <div className="bg-blue-500/10 text-blue-500 p-3 rounded-lg"><FiCheckCircle size={20} /></div>
            </div>
          </div>

          {/* 📈 Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sales Chart */}
            <div className="bg-white dark:bg-[#1e293b] rounded-xl border border-gray-200 dark:border-slate-700 p-5 shadow-sm lg:col-span-2">
              <h3 className="font-black text-sm mb-4">สถิติยอดจำหน่ายแบ่งตามงานบริการ</h3>
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { name: 'ยอดรวมทั้งหมด', sales: stats.totalSales },
                    { name: 'ค่าคอมมิชชัน', sales: stats.commission }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2c303d" />
                    <XAxis dataKey="name" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }} />
                    <Bar dataKey="sales" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Pie Chart */}
            <div className="bg-white dark:bg-[#1e293b] rounded-xl border border-gray-200 dark:border-slate-700 p-5 shadow-sm">
              <h3 className="font-black text-sm mb-4">สัดส่วนประเภทออเดอร์เซลส์</h3>
              <div className="h-60 flex flex-col justify-center items-center">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={chartData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex gap-4 text-[10px] mt-4 font-bold flex-wrap justify-center">
                  {chartData.map((item, idx) => (
                    <span key={idx} style={{ color: COLORS[idx % COLORS.length] }}>■ {item.name} ({item.value})</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 📅 Follow-up Reminders List */}
          <div className="bg-white dark:bg-[#1e293b] rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-5">
            <h3 className="font-black text-sm mb-4 flex items-center gap-2">
              <FiClock className="text-amber-500" /> รายชื่อลูกค้าครบกำหนด 6 เดือน (สำหรับ Sales ติดตามงาน)
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-100 dark:bg-[#0f172a] text-gray-500 border-b border-gray-200 dark:border-slate-700">
                  <tr>
                    <th className="px-5 py-3 font-bold">ชื่อลูกค้า</th>
                    <th className="px-5 py-3 font-bold">เบอร์โทรศัพท์</th>
                    <th className="px-5 py-3 font-bold">ที่อยู่</th>
                    <th className="px-5 py-3 font-bold text-center">วันที่ล้างรอบล่าสุด</th>
                    <th className="px-5 py-3 font-bold text-right">จำนวนวันผ่านมา</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {reminders.length > 0 ? (
                    reminders.map(rem => (
                      <tr key={rem.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                        <td className="px-5 py-3 font-bold">{rem.customerName}</td>
                        <td className="px-5 py-3">{rem.phone}</td>
                        <td className="px-5 py-3 max-w-[200px] truncate">{rem.address}</td>
                        <td className="px-5 py-3 text-center">{rem.appointmentDate}</td>
                        <td className="px-5 py-3 text-right text-red-500 font-bold">
                          {rem.days_since_service} วัน
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-8 text-gray-400">
                        ไม่พบข้อมูลลูกค้าครบกำหนดรอบล้างแอร์ในขณะนี้
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
